/**
 * User detail — who this customer is, what they can do, what they hold, and the four operator
 * controls over all of it.
 *
 * ## Contact details in full
 *
 * The email, the username and the phone are all shown whole wherever the API supplies them, and
 * copyable. That is the deliberate reversal of the app's masking rule: masking protects a
 * *customer* session from being used to harvest contact details, and an operator session is a
 * different thing — held by a named person, behind a separate credential, and used for exactly
 * the actions a masked number makes impossible. Where the API has not sent a full number, the
 * mask is labelled as a mask rather than passed off as the whole thing.
 *
 * ## Four controls, not one dropdown
 *
 * A suspension is a compliance decision, a lockout is a security measure the failed-PIN policy
 * usually sets on its own, dormancy is a lifecycle state, and verification is a KYC fact that is
 * not an account state at all. They are rendered as what they are. The first three write to one
 * `status` column and the page says so out loud rather than implying three independent switches;
 * verification is read-only here and points at the review, which is the only place that can
 * decide it with a document and a reason attached. See `lib/accountState.ts`.
 *
 * Every restrictive direction goes through a confirmation with a typed reason, exactly as the
 * suspend flow always has. Gated on `users:write`.
 *
 * ## Moving a balance
 *
 * Credit and Debit are gated on **`rates:write`** — the finance grant, the same one that sets a
 * rate or a fee, and the closest thing in `AdminPermission` to "this desk moves money". Both go
 * through `TypedConfirmModal` because this is real money leaving or entering a real customer's
 * wallet, and the amount is parsed with `BigInt` string surgery and never with `Number`.
 *
 * The customer's own activity row for a credit reads "Top Up" with the platform icon and carries
 * no admin attribution. That is the API's job, in its serializer, inside the same transaction as
 * the posting; nothing on this page can or should write a customer's activity feed.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { KYC_TIERS } from '../contracts/vocab.js';
import { adminCan } from '../contracts/permissions.js';
import type {
  AdminUserDetail,
  AdminUserSession,
  KycDocumentSummary,
  MoneyDTO,
  OperatorSettableState,
  SetAccountStateRequest,
} from '../contracts/types.js';
import {
  Badge,
  Button,
  Card,
  CopyButton,
  ErrorBox,
  Field,
  Input,
  LoadingState,
  Select,
  Textarea,
} from '../components/ui.js';
import { AdminAvatar } from '../components/AdminAvatar.js';
import { ContactPhone, PhoneProvenance } from '../components/ContactPhone.js';
import { DocumentViewer } from '../components/DocumentViewer.js';
import { KycStatusBadge, TierBadge, UserStatusBadge } from '../components/badges.js';
import { ConfirmModal } from '../components/Modal.js';
import { TypedConfirmModal } from '../components/TypedConfirmModal.js';
import { IconAlert, IconArrowLeft, IconEye, IconShield } from '../components/icons.js';
import { useToast } from '../components/Toast.js';
import { useAsync } from '../hooks/useAsync.js';
import {
  ACCOUNT_STATE_COPY,
  LOCK_DURATIONS,
  SHARED_COLUMN_NOTE,
  accountControlsOf,
  reasonRequiredFor,
  verificationOf,
  type AccountStateControl,
} from '../lib/accountState.js';
import {
  NARRATION_MAX,
  checkNarration,
  decimalsFor,
  newIdempotencyKey,
  overdraws,
  parseAdjustmentAmount,
  projectedBalance,
  type AdjustmentDirection,
} from '../lib/balanceAdjustment.js';
import { formatDateTime, humanize, relativeTime } from '../lib/format.js';
import { readIdentityVerification } from '../contracts/pending.js';
import { verificationSummary } from '../lib/identityVerification.js';
import { formatMoney, minorUnitsToInput } from '../lib/money.js';

export function UserDetailPage(): JSX.Element {
  const { id = '' } = useParams();
  const { api, session } = useAuth();
  const navigate = useNavigate();
  const canWrite = session !== null && adminCan(session.me.role, 'users:write');
  const canMoveMoney = session !== null && adminCan(session.me.role, 'rates:write');
  const canReadKyc = session !== null && adminCan(session.me.role, 'kyc:read');
  const canReviewKyc = session !== null && adminCan(session.me.role, 'kyc:review');

  const state = useAsync(() => api.getUser(id), [id]);
  const user = state.data;

  return (
    <>
      <button
        className="btn btn--ghost btn--sm"
        onClick={() => navigate('/users')}
        style={{ marginBottom: 16 }}
      >
        <IconArrowLeft size={15} /> Back to users
      </button>

      {state.loading ? (
        <LoadingState label="Loading customer…" />
      ) : state.error !== null ? (
        <ErrorBox message={state.error} />
      ) : user === null ? (
        <ErrorBox message="Customer not found." />
      ) : (
        <UserBody
          user={user}
          canWrite={canWrite}
          canMoveMoney={canMoveMoney}
          canReadKyc={canReadKyc}
          canReviewKyc={canReviewKyc}
          onChanged={(next) => state.setData(() => next)}
          onReload={state.reload}
        />
      )}
    </>
  );
}

function UserBody({
  user,
  canWrite,
  canMoveMoney,
  canReadKyc,
  canReviewKyc,
  onChanged,
  onReload,
}: {
  user: AdminUserDetail;
  canWrite: boolean;
  canMoveMoney: boolean;
  canReadKyc: boolean;
  canReviewKyc: boolean;
  onChanged: (next: AdminUserDetail) => void;
  onReload: () => void;
}): JSX.Element {
  const controls = accountControlsOf(user);
  const inForce = controls.find((c) => c.on) ?? null;

  return (
    <>
      <div className="page-head user-head">
        <div className="user-head__who">
          <AdminAvatar userId={user.id} name={user.displayName} avatar={user.avatar} size={64} />
          <div>
            <h1 className="page-head__title">{user.displayName ?? 'Unnamed customer'}</h1>
            <div className="user-head__badges">
              <UserStatusBadge status={user.status} />
              <TierBadge tier={user.tier} />
              <KycStatusBadge status={user.kycStatus} />
              {user.pinResetRequired && (
                <Badge tone="amber">PIN reset required</Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/*
        The state in force, said once at the top in the words that describe what it does to the
        customer. The badge above says which state; this says what it means, which is the thing
        an operator on a call actually has to relay.
      */}
      {inForce !== null && (
        <div className="state-banner" role="status">
          <IconAlert size={18} />
          <div>
            <strong>{ACCOUNT_STATE_COPY[user.status].label}.</strong> {inForce.effect}
            {inForce.reason !== null && <> Reason on file: {inForce.reason}</>}
          </div>
        </div>
      )}
      {user.status === 'closed' && (
        <div className="state-banner" role="status">
          <IconAlert size={18} />
          <div>
            <strong>Closed.</strong> {ACCOUNT_STATE_COPY.closed.effect}
          </div>
        </div>
      )}

      <div className="grid grid--2">
        <IdentityCard user={user} />
        <WalletCard user={user} />
      </div>

      <div style={{ marginTop: 16 }}>
        <AccountStateCard
          user={user}
          controls={controls}
          canWrite={canWrite}
          onChanged={onChanged}
        />
      </div>

      <div className="grid grid--2" style={{ marginTop: 16 }}>
        <KycCard
          user={user}
          canRead={canReadKyc}
          canReview={canReviewKyc}
          onGranted={onReload}
        />
        <BalanceCard user={user} canMoveMoney={canMoveMoney} onChanged={onChanged} onReload={onReload} />
      </div>

      <div style={{ marginTop: 16 }}>
        <Card title="Sessions">
          <SessionsTable sessions={user.sessions} />
        </Card>
      </div>
    </>
  );
}

// ── Identity ────────────────────────────────────────────────────────────────────────

function IdentityCard({ user }: { user: AdminUserDetail }): JSX.Element {
  return (
    <Card title="Identity">
      {/*
        Every contact value the DTO carries, rendered whole and copyable.

        The copy buttons are not decoration. A value read off a screen and retyped into a ticket
        is a value that can be wrong, and the one that gets retyped most is the customer id.
      */}
      <dl className="kv">
        <dt>Customer ID</dt>
        <dd className="kv__contact">
          <span className="mono" style={{ fontSize: 12 }}>
            {user.id}
          </span>
          <CopyButton value={user.id} label="customer ID" />
        </dd>
        <dt>Name</dt>
        <dd className="kv__contact">
          <span>{user.displayName ?? 'Unnamed customer'}</span>
          {user.displayName !== null && <CopyButton value={user.displayName} label="name" />}
        </dd>
        <dt>Email</dt>
        <dd className="kv__contact">
          <span className="mono">{user.email ?? <span className="muted">Not set</span>}</span>
          {user.email !== null && <CopyButton value={user.email} label="email" />}
        </dd>
        <dt>Username</dt>
        <dd className="kv__contact">
          <span className="mono">{user.username ?? <span className="muted">Not set</span>}</span>
          {user.username !== null && <CopyButton value={user.username} label="username" />}
        </dd>
        <dt>Phone</dt>
        <dd className="kv__contact">
          <ContactPhone row={user} />
        </dd>
        <dt>Joined</dt>
        <dd>{formatDateTime(user.createdAt)}</dd>
        <dt>Last active</dt>
        <dd>{relativeTime(user.lastActiveAt)}</dd>
      </dl>
      <PhoneProvenance row={user} />
    </Card>
  );
}

// ── Wallet and limits ───────────────────────────────────────────────────────────────

function WalletCard({ user }: { user: AdminUserDetail }): JSX.Element {
  const balances = user.balances.length > 0 ? user.balances : [user.walletBalance];
  return (
    <Card title="Balances & limits">
      <div className="balances">
        {balances.map((balance) => (
          <div className="balances__row" key={balance.currency}>
            <span className="balances__ccy mono">{balance.currency}</span>
            <span className="balances__amt">{formatMoney(balance)}</span>
          </div>
        ))}
      </div>
      <dl className="kv" style={{ marginTop: 16 }}>
        <dt>Single limit</dt>
        <dd className="num">{formatMoney(user.limits.single)}</dd>
        <dt>Daily limit</dt>
        <dd className="num">
          {formatMoney(user.limits.daily)}{' '}
          <Badge tone="neutral">{formatMoney(user.usage.daily)} used</Badge>
        </dd>
        <dt>Monthly limit</dt>
        <dd className="num">
          {formatMoney(user.limits.monthly)}{' '}
          <Badge tone="neutral">{formatMoney(user.usage.monthly)} used</Badge>
        </dd>
      </dl>
    </Card>
  );
}

// ── Account state ───────────────────────────────────────────────────────────────────

interface PendingStateChange {
  readonly control: AccountStateControl;
  readonly state: OperatorSettableState;
}

function AccountStateCard({
  user,
  controls,
  canWrite,
  onChanged,
}: {
  user: AdminUserDetail;
  controls: readonly AccountStateControl[];
  canWrite: boolean;
  onChanged: (next: AdminUserDetail) => void;
}): JSX.Element {
  const { api } = useAuth();
  const toast = useToast();
  const [pending, setPending] = useState<PendingStateChange | null>(null);
  const [reason, setReason] = useState('');
  const [lockMinutes, setLockMinutes] = useState(LOCK_DURATIONS[1]?.minutes ?? 60);
  const [busy, setBusy] = useState(false);
  const verification = verificationOf(user);

  function open(control: AccountStateControl): void {
    setReason('');
    setLockMinutes(LOCK_DURATIONS[1]?.minutes ?? 60);
    setPending({ control, state: control.action.state });
  }

  async function apply(): Promise<void> {
    if (pending === null) return;
    const trimmed = reason.trim();
    if (reasonRequiredFor(pending.state) && trimmed === '') {
      toast.error('A reason is required', 'A restriction nobody can explain is one nobody can safely lift.');
      return;
    }
    setBusy(true);
    try {
      const body: SetAccountStateRequest = {
        state: pending.state,
        reason: trimmed,
        ...(pending.state === 'locked' ? { lockMinutes } : {}),
      };
      const updated = await api.setAccountState(user.id, body);
      onChanged(updated);
      setPending(null);
      setReason('');
      toast.success(`Account is now ${ACCOUNT_STATE_COPY[pending.state].label.toLowerCase()}`);
    } catch (err) {
      toast.error('Could not change the account state', messageOf(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Account state">
      <p className="card__lead">
        {SHARED_COLUMN_NOTE}
        {!canWrite && ' Your role can view these but not change them.'}
      </p>

      <div className="states">
        {controls.map((control) => (
          <div className={`states__row${control.on ? ' states__row--on' : ''}`} key={control.id}>
            <div className="states__main">
              <div className="states__label">
                {control.label}
                {control.on && (
                  <Badge tone={control.id === 'lockout' ? 'red' : 'amber'} dot>
                    In force
                  </Badge>
                )}
              </div>
              <p className="states__effect">{control.effect}</p>
              {control.since !== null && (
                <p className="states__meta faint">
                  {control.id === 'lockout' ? 'Expires ' : 'Since '}
                  {formatDateTime(control.since)}
                </p>
              )}
              {control.reason !== null && (
                <p className="states__meta faint">Reason on file: {control.reason}</p>
              )}
              {control.blocked !== null && <p className="states__meta faint">{control.blocked}</p>}
            </div>
            <div className="states__action">
              <Button
                size="sm"
                variant={control.action.restrictive ? 'danger' : 'accent'}
                disabled={!canWrite || control.blocked !== null || busy}
                onClick={() => open(control)}
              >
                {control.action.label}
              </Button>
            </div>
          </div>
        ))}

        {/*
          Verification, alongside the three but visibly not one of them.

          It is a KYC fact rather than an account state, so it has no switch here: an account can
          be verified *and* suspended, and the single status column could only ever hold one of
          those. The control that decides it is the review, where the document and the reviewer's
          reason are recorded together, so this points there instead of duplicating it.
        */}
        <div className="states__row states__row--aside">
          <div className="states__main">
            <div className="states__label">
              Verification
              <KycStatusBadge status={verification.status} />
            </div>
            <p className="states__effect">{verification.effect}</p>
            <p className="states__meta faint">{verification.note}</p>
          </div>
          <div className="states__action">
            <Link className="btn btn--sm btn--ghost" to="/kyc">
              <IconShield size={14} /> Open KYC review
            </Link>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={pending !== null}
        title={pending === null ? '' : `${pending.control.action.label}?`}
        // Spread rather than passed straight through: `description` is an optional prop and
        // `exactOptionalPropertyTypes` refuses an explicit `undefined` for one, which is the
        // right rule — "absent" and "present but undefined" are different things.
        {...(pending === null ? {} : { description: pending.control.action.consequence })}
        confirmLabel={pending?.control.action.label ?? 'Confirm'}
        danger={pending?.control.action.restrictive === true}
        loading={busy}
        onConfirm={() => void apply()}
        onClose={() => {
          if (!busy) setPending(null);
        }}
      >
        {pending !== null && pending.state === 'locked' && (
          <Field label="Lockout length">
            <Select
              value={String(lockMinutes)}
              onChange={(e) => setLockMinutes(Number(e.target.value))}
            >
              {LOCK_DURATIONS.map((duration) => (
                <option key={duration.minutes} value={duration.minutes}>
                  {duration.label}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {pending !== null && (
          <Field
            label={
              reasonRequiredFor(pending.state)
                ? 'Reason (shown to operators, required)'
                : 'Note (shown to operators, optional)'
            }
          >
            <Textarea
              value={reason}
              maxLength={200}
              placeholder="e.g. Unusual payout velocity pending manual review"
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
        )}
      </ConfirmModal>
    </Card>
  );
}

// ── KYC ─────────────────────────────────────────────────────────────────────────────

function KycCard({
  user,
  canRead,
  canReview,
  onGranted,
}: {
  user: AdminUserDetail;
  canRead: boolean;
  canReview: boolean;
  onGranted: () => void;
}): JSX.Element {
  const [viewing, setViewing] = useState<KycDocumentSummary | null>(null);
  const documents = user.kycDocuments;
  const submissionId = documents[0]?.submissionId ?? null;

  /*
   * Where this customer's identity check actually got to.
   *
   * `identityVerification` is PROVISIONAL — see `contracts/pending.ts` — and `null` on every
   * API that has not shipped it, which is the ordinary case today. `verificationSummary`
   * degrades to the published `kycStatus` alone, so the panel below is correct either way and
   * simply says less.
   */
  const verification = verificationSummary(
    user.kycStatus,
    readIdentityVerification(user),
    documents.length,
  );

  return (
    <Card title="KYC">
      <dl className="kv">
        <dt>Status</dt>
        <dd>
          <KycStatusBadge status={user.kycStatus} />
        </dd>
        <dt>Tier</dt>
        <dd>
          <TierBadge tier={user.tier} />
        </dd>
        <dt>Submission</dt>
        <dd>
          {/*
            A hosted check leaves nothing here to open, which is the whole reason this line is
            no longer a bare link. Sending an operator to the review queue with a customer on
            the phone, to find an empty list, is worse than telling them there is nothing there.
          */}
          {verification.nothingToReview ? (
            <span className="muted">Checked away from here, so no document is on file</span>
          ) : submissionId === null ? (
            <span className="muted">No submission on file</span>
          ) : (
            <Link className="pcred-linkbtn" to="/kyc">
              Open in the review queue
            </Link>
          )}
        </dd>
      </dl>

      {/*
        The verdict in words, under the badges rather than instead of them.

        The badges say what. This says what it means for the account and what an operator can
        do about it, which is the question actually being asked when somebody opens this card:
        a customer is on the line saying they were refused, and "Rejected" is not an answer.

        Rendered for every state including `none`, because "no check has been done" is itself
        the answer to a support call about low limits.
      */}
      <div className={`idver idver--${verification.tone}`}>
        <div className="idver__head">
          <span className="idver__headline">{verification.headline}</span>
          {verification.at !== null && (
            <span className="faint idver__when">{relativeTime(verification.at)}</span>
          )}
        </div>
        <p className="idver__effect">{verification.effect}</p>
        {verification.reason !== null && (
          <p className="idver__reason">Reason on file: {verification.reason}</p>
        )}
      </div>

      {/*
        * Verifying somebody the queue cannot help with.
        *
        * Shown whatever their submission state, because the case this exists for is precisely
        * the one with nothing in the queue: a customer checked in person, or through a channel
        * this system never saw. Gated on `kyc:review` - the same grant that approves a real
        * submission, since this reaches the same outcome by a shorter route and a permission
        * that allowed the shortcut but not the documented path would be backwards.
        */}
      {canReview && <GrantTierPanel user={user} onGranted={onGranted} />}

      {!canRead ? (
        <p className="muted" style={{ marginTop: 12 }}>
          Your role cannot view identity documents.
        </p>
      ) : documents.length === 0 ? (
        <p className="muted" style={{ marginTop: 12 }}>
          No identity documents are on file for this customer.
        </p>
      ) : (
        <>
          <div className="docs">
            {documents.map((doc) => (
              <button
                type="button"
                className="docs__item"
                key={doc.id}
                onClick={() => setViewing(doc)}
              >
                <IconEye size={15} />
                <span className="docs__name">{humanize(doc.kind)}</span>
                <span className="docs__meta faint mono">
                  {doc.mime === 'application/pdf' ? 'PDF' : 'Image'}
                </span>
              </button>
            ))}
          </div>
          {/*
            Said on screen, not only in the code. A reviewer who knows the read is recorded
            behaves differently from one who assumes it is not, and that difference is the whole
            control on a screen that can show somebody's passport.
          */}
          <p className="docs__note">
            Opening a document is recorded against your name, with the document it was.
          </p>
        </>
      )}

      {viewing !== null && (
        <DocumentViewer document={viewing} onClose={() => setViewing(null)} />
      )}
    </Card>
  );
}

// ── Credit and debit ────────────────────────────────────────────────────────────────

function BalanceCard({
  user,
  canMoveMoney,
  onChanged,
  onReload,
}: {
  user: AdminUserDetail;
  canMoveMoney: boolean;
  onChanged: (next: AdminUserDetail) => void;
  onReload: () => void;
}): JSX.Element {
  const { api } = useAuth();
  const toast = useToast();
  const balances = user.balances.length > 0 ? user.balances : [user.walletBalance];

  const [direction, setDirection] = useState<AdjustmentDirection>('credit');
  const [currency, setCurrency] = useState(balances[0]?.currency ?? 'NGN');
  const [amount, setAmount] = useState('');
  const [narration, setNarration] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  /*
   * Minted once, when the dialog is armed, and reused for every retry of that same attempt.
   * Minting inside the submit handler would defeat the whole mechanism: two clicks would carry
   * two keys and the server would honour both.
   */
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);

  const wallet: MoneyDTO = balances.find((b) => b.currency === currency) ?? balances[0] ?? user.walletBalance;
  const parsed = useMemo(() => parseAdjustmentAmount(amount, currency), [amount, currency]);
  const narrationCheck = checkNarration(narration);
  const minor = parsed.ok ? parsed.minorUnits : null;
  const projected = minor === null ? null : projectedBalance(wallet, minor, direction);
  const wouldOverdraw = minor !== null && overdraws(wallet, minor, direction);
  const ready = parsed.ok && narrationCheck.ok && canMoveMoney;

  async function submit(): Promise<void> {
    if (minor === null || !narrationCheck.ok) return;
    setBusy(true);
    try {
      const result = await api.adjustBalance(user.id, {
        direction,
        amount: { currency: wallet.currency, amount: minor },
        narration: narration.trim(),
        idempotencyKey,
      });
      setConfirming(false);
      setAmount('');
      setNarration('');
      setIdempotencyKey(newIdempotencyKey());
      toast.success(
        direction === 'credit' ? 'Balance credited' : 'Balance debited',
        `${formatMoney(result.balance)} now held. Reference ${result.reference}.`,
      );
      /*
       * Reloaded rather than patched from the response. The receipt carries the balance in one
       * currency, and this page shows every state that write can move — the balance list, the
       * headline, the usage figures. Patching one field would leave the rest of the record
       * describing the moment before the money moved.
       */
      onReload();
      onChanged({ ...user, walletBalance: result.balance.currency === user.walletBalance.currency ? result.balance : user.walletBalance });
    } catch (err) {
      toast.error('Could not move the balance', messageOf(err));
    } finally {
      setBusy(false);
    }
  }

  if (!canMoveMoney) {
    return (
      <Card title="Move a balance">
        <p className="muted">
          Your role cannot credit or debit a customer. This needs the finance grant.
        </p>
      </Card>
    );
  }

  return (
    <Card title="Move a balance">
      <p className="card__lead">
        A manual credit or debit, posted as a double entry against the house account and recorded
        against your name with the narration you write. The customer sees an ordinary top up in
        their activity, with nothing about this console on it.
      </p>

      <div className="adjust">
        <div className="adjust__dirs" role="group" aria-label="Direction">
          <Button
            size="sm"
            variant={direction === 'credit' ? 'accent' : 'default'}
            onClick={() => setDirection('credit')}
          >
            Credit
          </Button>
          <Button
            size="sm"
            variant={direction === 'debit' ? 'danger' : 'default'}
            onClick={() => setDirection('debit')}
          >
            Debit
          </Button>
        </div>

        {balances.length > 1 && (
          <Field label="Wallet">
            <Select
              value={currency}
              // Narrowed back to the currency union rather than widened to `string`. The options
              // are built from this customer's own balances, so the value is always one of them.
              onChange={(e) => setCurrency(e.target.value as MoneyDTO['currency'])}
            >
              {balances.map((balance) => (
                <option key={balance.currency} value={balance.currency}>
                  {balance.currency} · {formatMoney(balance)}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <Field label={`Amount (${wallet.currency})`} htmlFor="adjust-amount">
          <Input
            id="adjust-amount"
            className="mono"
            inputMode="decimal"
            autoComplete="off"
            placeholder={minorUnitsToInput('0', decimalsFor(wallet.currency))}
            value={amount}
            disabled={busy}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Field>
        {!parsed.ok && amount.trim() !== '' && <p className="adjust__bad">{parsed.error}</p>}

        <Field label={`Narration (required, up to ${String(NARRATION_MAX)} characters)`}>
          <Textarea
            value={narration}
            maxLength={NARRATION_MAX}
            placeholder="e.g. Goodwill credit for the failed payout on 18 Aug, ticket 4471"
            disabled={busy}
            onChange={(e) => setNarration(e.target.value)}
          />
        </Field>
        {!narrationCheck.ok && narration.trim() !== '' && (
          <p className="adjust__bad">{narrationCheck.error}</p>
        )}

        {projected !== null && (
          <div className={`adjust__result${wouldOverdraw ? ' adjust__result--warn' : ''}`}>
            <span className="faint">Balance after this</span>
            <strong>{formatMoney(projected)}</strong>
            {wouldOverdraw && (
              <span className="adjust__warn">
                This takes the customer below zero. The API decides whether that is allowed.
              </span>
            )}
          </div>
        )}

        <Button
          variant={direction === 'credit' ? 'accent' : 'danger'}
          disabled={!ready || busy}
          onClick={() => setConfirming(true)}
        >
          {direction === 'credit' ? 'Credit this customer' : 'Debit this customer'}
        </Button>
      </div>

      <TypedConfirmModal
        open={confirming}
        title={direction === 'credit' ? 'Credit this balance?' : 'Debit this balance?'}
        // The amount, retyped. A speed bump placed exactly where muscle memory is, and the one
        // that makes an operator read the figure a second time rather than the button.
        phrase={amount.trim()}
        headlineCount={0}
        headlineText={minor === null ? '' : formatMoney({ currency: wallet.currency, amount: minor })}
        headlineNoun={direction === 'credit' ? `into ${user.displayName ?? 'this wallet'}` : `out of ${user.displayName ?? 'this wallet'}`}
        warning={
          direction === 'credit'
            ? 'This posts real money into a customer wallet. It can be reversed by a matching debit, but it cannot be undone.'
            : 'This takes real money out of a customer wallet. It can be reversed by a matching credit, but it cannot be undone.'
        }
        confirmLabel={direction === 'credit' ? 'Credit now' : 'Debit now'}
        loading={busy}
        onConfirm={() => void submit()}
        onClose={() => setConfirming(false)}
      >
        <dl className="kv">
          <dt>Customer</dt>
          <dd>{user.displayName ?? user.id}</dd>
          <dt>Wallet</dt>
          <dd className="mono">{wallet.currency}</dd>
          <dt>Balance now</dt>
          <dd>{formatMoney(wallet)}</dd>
          <dt>Balance after</dt>
          <dd>{projected === null ? '–' : formatMoney(projected)}</dd>
          <dt>Narration</dt>
          <dd>{narration.trim()}</dd>
        </dl>
      </TypedConfirmModal>
    </Card>
  );
}

// ── Sessions ────────────────────────────────────────────────────────────────────────

/**
 * Where this customer is signed in, newest first, live and ended alike.
 *
 * Support's two questions are "which handset are they calling about" and "did the old one really
 * get signed out", and neither is answerable from a single `lastActiveAt` timestamp. The label is
 * the server's, byte-for-byte the same string on the customer's own security screen, so reading
 * one back to the other works.
 *
 * There is no device identifier here and no revoke button. Ending someone else's session is the
 * customer's own action; an operator who needs it gone suspends the account, which is audited and
 * reversible. Showing the identifier would put the value that binds a refresh token to a handset
 * on a screen support staff read aloud.
 */
function SessionsTable({ sessions }: { sessions: readonly AdminUserSession[] }): JSX.Element {
  if (sessions.length === 0) {
    return <p className="muted">This customer has never signed in on a device.</p>;
  }

  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Session</th>
            <th>Device</th>
            <th>Last seen</th>
            <th>State</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((session) => (
            <tr key={session.id}>
              <td>{session.label}</td>
              <td>
                {session.name}
                {session.lastSeenLocation !== null && (
                  <span className="muted"> · {session.lastSeenLocation}</span>
                )}
              </td>
              <td title={formatDateTime(session.lastSeenAt)}>{relativeTime(session.lastSeenAt)}</td>
              <td>
                {session.revokedAt === null ? (
                  <Badge tone="green">Active</Badge>
                ) : (
                  <Badge tone="neutral">Signed out {relativeTime(session.revokedAt)}</Badge>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// `AdminUserDetail` is imported for the shapes above rather than used directly; the page reads
// the widened row so the customer's photo is available where the API sends one.
export type { AdminUserDetail };

/**
 * Raising a tier for somebody the review queue cannot help with.
 *
 * ## Why the reason is a required field and not a nicety
 *
 * An approval in the queue has documents behind it: the evidence is already in the system and
 * the note is optional. This has none, so the sentence typed here *is* the evidence. A grant
 * with no reason would leave a customer holding a tier with nothing anywhere saying why - which
 * is the first thing anybody auditing this will look for and the one thing they would not find.
 *
 * ## Why it will not lower a tier
 *
 * The server refuses it, and the select offers only tiers above the customer's current one. A
 * grant that could also demote would be a punishment sharing a button, an audit key and a
 * confirmation dialog with a verification - and somebody would eventually reach for the wrong
 * one. Restricting a customer is its own action, with its own reason and its own trail.
 */
function GrantTierPanel({
  user,
  onGranted,
}: {
  user: AdminUserDetail;
  onGranted: () => void;
}): JSX.Element {
  const { api } = useAuth();
  /*
   * Only tiers above the one they hold.
   *
   * `KYC_TIERS` is declared in rank order, so position *is* rank and no separate table can
   * drift out of step with it. Offering a lower tier would put a demotion in a control called
   * "grant", which the server refuses anyway - this stops it being askable in the first place.
   */
  const currentRank = KYC_TIERS.indexOf(user.tier);
  const higher = KYC_TIERS.filter((_option, rank) => rank > currentRank);
  const [tier, setTier] = useState<(typeof KYC_TIERS)[number] | ''>('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (higher.length === 0) {
    return (
      <p className="muted" style={{ marginTop: 12 }}>
        This customer already holds the highest tier.
      </p>
    );
  }

  const submit = async (): Promise<void> => {
    if (tier === '' || reason.trim().length < 8) return;
    setBusy(true);
    setError(null);
    try {
      await api.grantTier(user.id, { tier, reason: reason.trim() });
      setDone(true);
      setReason('');
      setTier('');
      onGranted();
    } catch (err) {
      // Named. A grant that silently does nothing is one an operator repeats, and every repeat
      // writes another audit row for a decision they only made once.
      setError(err instanceof Error ? err.message : 'Could not grant that tier.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grant-tier" style={{ marginTop: 16 }}>
      <h4 style={{ margin: '0 0 4px' }}>Verify without a submission</h4>
      <p className="muted" style={{ margin: '0 0 10px' }}>
        For a customer verified out of band. The reason is recorded against them permanently and
        is what a compliance review will read.
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <select
          className="input"
          aria-label="Tier to grant"
          value={tier}
          disabled={busy}
          onChange={(event) => {
            setDone(false);
            setTier(event.target.value as (typeof KYC_TIERS)[number] | '');
          }}
          style={{ maxWidth: 160 }}
        >
          <option value="">Choose a tier…</option>
          {higher.map((option) => (
            <option key={option} value={option}>
              {option.replace('tier_', 'Tier ')}
            </option>
          ))}
        </select>

        <input
          className="input"
          aria-label="Why this customer is being verified"
          placeholder="Verified in branch, passport seen, ref…"
          value={reason}
          maxLength={160}
          disabled={busy}
          onChange={(event) => {
            setDone(false);
            setReason(event.target.value);
          }}
          style={{ flex: 1, minWidth: 240 }}
        />

        <button
          type="button"
          className="btn btn--primary"
          disabled={busy || tier === '' || reason.trim().length < 8}
          onClick={() => void submit()}
        >
          {busy ? 'Granting…' : 'Grant tier'}
        </button>
      </div>

      {error !== null && <ErrorBox message={error} />}
      {done && (
        <p className="muted" style={{ marginTop: 8 }}>
          Tier granted and recorded.
        </p>
      )}
    </div>
  );
}
